import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";
import moment, { Moment } from "moment";

import { classNames } from "primereact/utils";

import { DateIs } from "../../../../../utils/date-utils";
import { translationKeys } from "../../../../models/app-translation-keys";
import { IChatThreadExtendedModel } from "../../../../models/interfaces";
import { ICustomKnowledgeBaseReadChatbot } from "../../../../stores";
import { useStore } from "../../../../stores/StoreContext";
import { Separator } from "./Separator/Separator";
import { ThreadItem } from "./ThreadItem/ThreadItem";

import "./ThreadHistoryList.scss";

interface IThreadHistoryList {
  threads: IChatThreadExtendedModel[];
  knowledgeBases: ICustomKnowledgeBaseReadChatbot[];
  activeThreadId?: string;
  showDateSeparators?: boolean;
}

const RawThreadHistoryList: FunctionComponent<IThreadHistoryList> = (props: IThreadHistoryList) => {
  const { t } = useTranslation();
  const { userStore, threadStore } = useStore();

  const [sortedThreads, setSortedThreads] = useState<IChatThreadExtendedModel[]>([]);

  useEffect(() => {
    setSortedThreads(sortThreads(props.threads));
  }, [props.threads]);

  const dateChecksMap: Map<string, { used: boolean; func: (date: Moment) => boolean }> = new Map([
    ["today", { used: false, func: DateIs.today }],
    ["yesterday", { used: false, func: DateIs.yesterday }],
    ["lastWeek", { used: false, func: DateIs.lastWeek }],
    ["p30Days", { used: false, func: DateIs.previous30Days }],
    ["currentYear", { used: false, func: DateIs.currentYear }],
    ["beforeCurrentYear", { used: false, func: DateIs.beforeCurrentYear }],
  ]);

  const sortThreads = (threads: IChatThreadExtendedModel[]) => {
    return threads.sort((a, b) => {
      const dateA = a.lastActivity ? a.lastActivity.valueOf() : 0;
      const dateB = b.lastActivity ? b.lastActivity.valueOf() : 0;
      return dateB - dateA;
    });
  };

  const generateSeparatorMemo = useMemo(
    () =>
      (thread: IChatThreadExtendedModel): string | undefined => {
        const momThreadDate = moment(thread.lastActivity);
        for (const [key, dateCheck] of dateChecksMap) {
          if (!dateCheck.used) {
            if (dateCheck.func(momThreadDate)) {
              dateCheck.used = true;
              return key;
            }
          }
        }
        return undefined;
      },
    [dateChecksMap, userStore.currentLanguage],
  );

  const separatorTemplate = (thread: IChatThreadExtendedModel, index: number) => {
    const separatorKey = generateSeparatorMemo(thread);

    return (
      !!separatorKey && (
        <li className="separator" key={"separator-" + index + "-" + separatorKey}>
          <Separator separatorKey={separatorKey} />
        </li>
      )
    );
  };

  const threadItemTemplate = (thread: IChatThreadExtendedModel) => {
    const knowledgeBase = props.knowledgeBases.find(kb => kb.id === thread.knowledgeBaseId);
    const isActive = thread.id === props.activeThreadId;

    return (
      <li className={classNames("thread", { active: isActive })} key={thread.id}>
        <ThreadItem thread={thread} knowledgeBase={knowledgeBase} isActive={isActive} />
      </li>
    );
  };

  if (!sortedThreads.length) {
    return (
      <span className="no-threads" tabIndex={-1}>
        {t(translationKeys.sidebarThreads.noThreads)}
      </span>
    );
  }

  return (
    <ul className="thread-list" tabIndex={-1}>
      {sortedThreads.map((thread, index) => {
        const updatedThread = threadStore.threads.find(t => t.id === thread.id) || thread;

        return [props.showDateSeparators && separatorTemplate(updatedThread, index), threadItemTemplate(updatedThread)];
      })}
    </ul>
  );
};

export const ThreadHistoryList = observer(RawThreadHistoryList);
